import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, FileText, Edit, Trash2, Link as LinkIcon, AlertCircle, LogOut, BookOpen, BarChart3, List, Key } from 'lucide-react'
import { fetchTemplates, deleteTemplate, changePassword } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import type { Template } from '../types'

export function AdminPage() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)
  const [changingPassword, setChangingPassword] = useState(false)

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      const data = await fetchTemplates()
      setTemplates(data)
    } catch {
      setError('Erro ao carregar modelos')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Tem a certeza que deseja eliminar o modelo "${name}"?`)) {
      return
    }

    setDeleting(id)
    try {
      await deleteTemplate(id)
      setTemplates((prev) => prev.filter((t) => t.id !== id))
    } catch {
      alert('Erro ao eliminar o modelo')
    } finally {
      setDeleting(null)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleChangePassword = async () => {
    setPasswordError(null)
    setPasswordSuccess(null)

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Preencha todos os campos')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('As passwords não coincidem')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('A nova password deve ter pelo menos 8 caracteres')
      return
    }

    setChangingPassword(true)
    try {
      const result = await changePassword(currentPassword, newPassword)
      setPasswordSuccess(result.message || 'Password validada com sucesso!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Erro ao alterar password')
    } finally {
      setChangingPassword(false)
    }
  }

  const closePasswordModal = () => {
    setShowPasswordModal(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError(null)
    setPasswordSuccess(null)
  }

  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || 'Geral'
    if (!acc[category]) acc[category] = []
    acc[category].push(template)
    return acc
  }, {} as Record<string, Template[]>)

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Painel de Administração</h1>
          <div className="flex items-center gap-3">
            <Link to="/admin/stats" className="btn-secondary gap-2">
              <BarChart3 className="w-5 h-5" />
              Estatísticas
            </Link>
            <Link to="/admin/autocomplete" className="btn-secondary gap-2">
              <List className="w-5 h-5" />
              Listas
            </Link>
            <Link to="/admin/blog" className="btn-secondary gap-2">
              <BookOpen className="w-5 h-5" />
              Blog
            </Link>
            <Link to="/admin/template/new" className="btn-primary gap-2">
              <Plus className="w-5 h-5" />
              Novo Modelo
            </Link>
            <button 
              onClick={() => setShowPasswordModal(true)} 
              className="btn-secondary gap-2"
              title="Alterar Password"
            >
              <Key className="w-5 h-5" />
            </button>
            <button onClick={handleLogout} className="btn-secondary gap-2">
              <LogOut className="w-5 h-5" />
              Sair
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="card p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : templates.length === 0 ? (
          <div className="card p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Nenhum modelo criado ainda.</p>
            <Link to="/admin/template/new" className="btn-primary inline-flex gap-2">
              <Plus className="w-5 h-5" />
              Criar Primeiro Modelo
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <div key={category} className="card">
                <div className="p-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900">{category}</h2>
                  <p className="text-sm text-gray-500">{categoryTemplates.length} modelo(s)</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {categoryTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{template.name}</h3>
                          <p className="text-sm text-gray-500">
                            {template.fields.length} campos ({template.fields.filter(f => f.required).length} obrigatórios) · Criado em{' '}
                            {new Date(template.created_at).toLocaleDateString('pt-PT')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/generate/${template.id}`}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Gerar Link"
                        >
                          <LinkIcon className="w-5 h-5" />
                        </Link>
                        <Link
                          to={`/admin/template/${template.id}`}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-5 h-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(template.id, template.name)}
                          disabled={deleting === template.id}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Eliminar"
                        >
                          {deleting === template.id ? (
                            <div className="w-5 h-5 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-primary-600" />
              Alterar Password
            </h3>

            {passwordError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {passwordSuccess}
                <p className="mt-2 text-xs">
                  Para aplicar a nova password, execute no terminal:<br />
                  <code className="bg-green-100 px-1 rounded">wrangler secret put ADMIN_PASSWORD</code>
                </p>
              </div>
            )}

            <div>
              <label className="label">Password Atual</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="label">Nova Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="Mínimo 8 caracteres"
              />
            </div>

            <div>
              <label className="label">Confirmar Nova Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={closePasswordModal}
                className="btn-secondary"
                disabled={changingPassword}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                className="btn-primary gap-2"
                disabled={changingPassword}
              >
                {changingPassword ? 'A validar...' : 'Alterar Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
